import { useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useAuth, ROLE_LABELS, homeLocationOf } from "@/lib/auth";
import {
  LayoutDashboard, PackagePlus, ClipboardCheck, Boxes,
  Blend, Truck, FileBarChart, LogOut, Menu, X, ArrowRightLeft, UserCog, Trash2,
} from "lucide-react";

// Per-role navigation lists.
const NAV_BY_ROLE = {
  // Location users: scoped operations on their own warehouse (Inward, Transfer, Mixing, Stock Register, Dispatch)
  factory_user: [
    { to: "/", icon: LayoutDashboard, label: "Dashboard" },
    { to: "/inward", icon: PackagePlus, label: "Stock Inward" },
    { to: "/transfer", icon: ArrowRightLeft, label: "Stock Transfer" },
    { to: "/stock", icon: Boxes, label: "Stock Register" },
    { to: "/mixing", icon: Blend, label: "Batch Mixing" },
    { to: "/dispatch", icon: Truck, label: "Dispatch" },
  ],
  // Legacy store_manager: treated like a factory user
  store_manager: [
    { to: "/", icon: LayoutDashboard, label: "Dashboard" },
    { to: "/inward", icon: PackagePlus, label: "Stock Inward" },
    { to: "/transfer", icon: ArrowRightLeft, label: "Stock Transfer" },
    { to: "/stock", icon: Boxes, label: "Stock Register" },
    { to: "/mixing", icon: Blend, label: "Batch Mixing" },
    { to: "/dispatch", icon: Truck, label: "Dispatch" },
  ],
  baikampady_user: [
    { to: "/", icon: LayoutDashboard, label: "Dashboard" },
    { to: "/inward", icon: PackagePlus, label: "Stock Inward" },
    { to: "/transfer", icon: ArrowRightLeft, label: "Stock Transfer" },
    { to: "/stock", icon: Boxes, label: "Stock Register" },
    { to: "/mixing", icon: Blend, label: "Batch Mixing" },
    { to: "/dispatch", icon: Truck, label: "Dispatch" },
  ],
  maghveera_user: [
    { to: "/", icon: LayoutDashboard, label: "Dashboard" },
    { to: "/inward", icon: PackagePlus, label: "Stock Inward" },
    { to: "/transfer", icon: ArrowRightLeft, label: "Stock Transfer" },
    { to: "/stock", icon: Boxes, label: "Stock Register" },
    { to: "/mixing", icon: Blend, label: "Batch Mixing" },
    { to: "/dispatch", icon: Truck, label: "Dispatch" },
  ],
  qc_manager: [
    { to: "/", icon: LayoutDashboard, label: "Dashboard" },
    { to: "/qc", icon: ClipboardCheck, label: "QC Approval" },
    { to: "/stock", icon: Boxes, label: "Stock Register" },
    { to: "/dispatch", icon: Truck, label: "Dispatch" },
    { to: "/mixing", icon: Blend, label: "Batch Mixing" },
    { to: "/transfer", icon: ArrowRightLeft, label: "Stock Transfer" },
    { to: "/reports", icon: FileBarChart, label: "Reports" },
    { to: "/trash", icon: Trash2, label: "Recently Deleted" },
  ],
  top_management: [
    { to: "/", icon: LayoutDashboard, label: "Dashboard" },
    { to: "/stock", icon: Boxes, label: "Stock Register" },
    { to: "/dispatch", icon: Truck, label: "Dispatch Register" },
    { to: "/mixing", icon: Blend, label: "Batch Mixing" },
    { to: "/transfer", icon: ArrowRightLeft, label: "Stock Transfer" },
    { to: "/reports", icon: FileBarChart, label: "Reports" },
    { to: "/users", icon: UserCog, label: "User Management" },
    { to: "/trash", icon: Trash2, label: "Recently Deleted" },
  ],
  super_admin: [
    { to: "/", icon: LayoutDashboard, label: "Dashboard" },
    { to: "/inward", icon: PackagePlus, label: "Stock Inward" },
    { to: "/qc", icon: ClipboardCheck, label: "QC Approval" },
    { to: "/stock", icon: Boxes, label: "Stock Register" },
    { to: "/dispatch", icon: Truck, label: "Dispatch" },
    { to: "/mixing", icon: Blend, label: "Batch Mixing" },
    { to: "/transfer", icon: ArrowRightLeft, label: "Stock Transfer" },
    { to: "/reports", icon: FileBarChart, label: "Reports" },
    { to: "/users", icon: UserCog, label: "User Management" },
    { to: "/trash", icon: Trash2, label: "Recently Deleted" },
  ],
};

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);

  const visible = NAV_BY_ROLE[user.role] || [];
  const home = homeLocationOf(user);

  const SidebarContent = (
    <>
      <div className="p-5 border-b flex items-center justify-between" style={{ borderColor: "var(--border)" }}>
        <div className="flex items-center gap-2.5">
          <img
            src="/logo.jpg"
            alt="SM Marine"
            className="flex-shrink-0"
            style={{ width: 40, height: 40, objectFit: "contain", background: "#fff", padding: 2, borderRadius: 8, boxShadow: "var(--shadow-sm)" }}
          />
          <div className="min-w-0">
            <div className="font-heading font-bold text-sm tracking-tight" style={{ color: "var(--text)" }}>SM MARINE</div>
            <div className="text-[10px] font-mono truncate" style={{ color: "var(--text-dim)" }}>PRODUCTS — WMS</div>
          </div>
        </div>
        <button className="btn btn-ghost lg:hidden" style={{ padding: "4px" }} onClick={() => setOpen(false)} data-testid="close-drawer-button" aria-label="Close menu">
          <X size={18} />
        </button>
      </div>

      <nav className="flex-1 p-3 space-y-0.5 overflow-y-auto">
        <div className="text-[10px] font-mono px-3 py-2" style={{ color: "var(--text-dim)" }}>// NAVIGATION</div>
        {visible.map(({ to, icon: Icon, label }) => (
          <NavLink
            key={to}
            to={to}
            end={to === "/"}
            onClick={() => setOpen(false)}
            data-testid={`nav-${label.toLowerCase().replace(/\s|\(|\)/g, "-")}`}
            className={({ isActive }) => `flex items-center gap-3 px-3 py-2 text-sm transition-colors rounded-md ${isActive ? "is-active" : ""}`}
            style={({ isActive }) => ({
              background: isActive ? "var(--brand-blue-soft)" : "transparent",
              color: isActive ? "var(--brand-blue)" : "var(--text-muted)",
              fontWeight: isActive ? 600 : 500,
              borderLeft: isActive ? "3px solid var(--brand-blue)" : "3px solid transparent",
            })}
          >
            <Icon size={16} strokeWidth={2} />
            <span>{label}</span>
          </NavLink>
        ))}
      </nav>

      <div className="p-3 border-t" style={{ borderColor: "var(--border)" }}>
        <div className="px-2 py-2 mb-2">
          <div className="text-[10px] font-mono" style={{ color: "var(--text-dim)" }}>// SIGNED IN</div>
          <div className="text-sm font-medium mt-1 truncate" style={{ color: "var(--text)" }} data-testid="current-user-name">{user.name}</div>
          <div className="text-xs font-mono truncate" style={{ color: "var(--brand-blue)" }} data-testid="current-user-role">
            {ROLE_LABELS[user.role]}{home ? ` · ${home}` : ""}
          </div>
        </div>
        <button className="btn btn-ghost w-full justify-start" onClick={async () => { await logout(); nav("/login"); }} data-testid="logout-button">
          <LogOut size={14} /> Sign out
        </button>
      </div>
    </>
  );

  return (
    <div className="flex h-screen" style={{ background: "var(--bg)" }}>
      <aside className="hidden lg:flex w-60 flex-col" style={{ background: "var(--surface)", borderRight: "1px solid var(--border)" }}>
        {SidebarContent}
      </aside>

      {open && (
        <>
          <div className="fixed inset-0 z-40 lg:hidden" style={{ background: "rgba(13,42,78,0.45)" }} onClick={() => setOpen(false)} data-testid="drawer-backdrop" />
          <aside className="fixed inset-y-0 left-0 z-50 w-64 flex flex-col lg:hidden" style={{ background: "var(--surface)", borderRight: "1px solid var(--border)", boxShadow: "var(--shadow-lg)" }}>
            {SidebarContent}
          </aside>
        </>
      )}

      <main className="flex-1 overflow-y-auto min-w-0">
        <div className="lg:hidden sticky top-0 z-30 flex items-center justify-between px-4 py-3 border-b" style={{ background: "var(--surface)", borderColor: "var(--border)" }}>
          <button className="btn btn-ghost" style={{ padding: "6px" }} onClick={() => setOpen(true)} data-testid="open-drawer-button" aria-label="Open menu">
            <Menu size={20} />
          </button>
          <div className="flex items-center gap-2">
            <img
              src="/logo.jpg"
              alt="SM Marine"
              style={{ width: 28, height: 28, objectFit: "contain", background: "#fff", padding: 1, borderRadius: 6 }}
            />
            <div className="font-heading font-bold text-sm tracking-tight" style={{ color: "var(--text)" }}>SM MARINE</div>
          </div>
          <div className="w-7" />
        </div>
        {children}
      </main>
    </div>
  );
}
